import { useState } from "react";
import { Link } from "react-router-dom";
import { SignInButton, SignedOut } from "@clerk/clerk-react";
import {
  LucideEye,
  LucideClock,
  LucideX,
  LucideArrowRight,
} from "lucide-react";
import { pdfjs } from "react-pdf";
import { PDFPreview } from "@/reports/shared/PDFPreview";
import { REPORT_TYPES } from "@/constants/reportTypes";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

export default function FreeTrialPicker() {
  const [previewPdf, setPreviewPdf] = useState(null);

  function openPreview(pdfUrl) {
    setPreviewPdf(pdfUrl);
  }

  function closePreview() {
    setPreviewPdf(null);
  }

  return (
    <div className="free-trial-picker-root">
      {/* ── HEADER ─────────────────────────────────────────── */}
      <div className="free-trial-picker-header">
        <span className="hero-eyebrow">Free trial</span>
        <h1 className="free-trial-picker-heading">
          Choose a report card to try
        </h1>
        <p className="free-trial-picker-sub">
          No account required. Pick a template, fill it in, and download the
          PDF - all for free.
        </p>
      </div>

      {/* ── CARDS ──────────────────────────────────────────── */}
      <div className="free-trial-picker-grid">
        {REPORT_TYPES.map((type, i) => (
          <div
            key={type.value}
            className={[
              "free-trial-type-card",
              type.disabled ? "disabled" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            style={{ animationDelay: `${i * 60}ms` }}
          >
            {/* Card header row */}
            <div className="free-trial-card-top">
              <div className="free-trial-card-labels">
                <span className="free-trial-card-label">{type.label}</span>
                <span className="free-trial-card-badge">{type.subtitle}</span>
                {type.disabled && (
                  <span className="free-trial-coming-soon">
                    <LucideClock size={10} />
                    Coming soon
                  </span>
                )}
              </div>

              {/* Preview button - only for enabled types with a PDF */}
              {type.pdfPreview && !type.disabled && (
                <button
                  type="button"
                  onClick={() => openPreview(type.pdfPreview)}
                  title="Preview this report card"
                  className="free-trial-preview-btn"
                >
                  <LucideEye size={14} />
                  Preview
                </button>
              )}
            </div>

            <p className="free-trial-card-desc">{type.description}</p>

            {/* CTA */}
            <div className="free-trial-card-footer">
              {type.disabled ? (
                <button disabled className="free-trial-try-btn disabled">
                  Try this report
                  <LucideArrowRight size={14} />
                </button>
              ) : (
                <Link to={type.freeTrialPath}>
                  <button className="free-trial-try-btn">
                    Try this report
                    <LucideArrowRight size={14} />
                  </button>
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── PDF PREVIEW MODAL ──────────────────────────────── */}
      {previewPdf && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closePreview}
          />
          <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden" style={{ height: "80vh" }}>
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border shrink-0">
              <p className="text-sm font-medium text-foreground">
                Report Card Preview
              </p>
              <button
                onClick={closePreview}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <LucideX size={15} />
              </button>
            </div>

            {/* Modal body - canvas-rendered PDF (read-only, no editable fields) */}
            <div className="flex-1 overflow-hidden">
              <PDFPreview src={previewPdf} defaultPageCount={2} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
